import { useCallback, useEffect, useMemo, useState } from 'react'
import { Activity, AlertTriangle, Boxes, ExternalLink, LayoutGrid, RefreshCw, Search, Table2, Users } from 'lucide-react'
import { Button } from '../../../components/ui/Button'
import { Card } from '../../../components/ui/Card'
import { reauthorizeAfterUnauthorized } from '../../../auth/oidc'
import { clearOidcReauthorization, loadSession } from '../../../auth/state'
import { useI18n } from '../../../utils/i18n'

const AUTO_REFRESH_MS = 30_000

function unwrap(body) {
  if (body && typeof body.code === 'number') {
    return { ok: body.code === 0, error: body.code === 0 ? '' : body.msg, data: body.data || {} }
  }
  return { ok: Boolean(body?.ok), error: body?.error || '', data: body || {} }
}

async function loadRegistry(token) {
  const response = await fetch('/iam-admin/platforms', {
    cache: 'no-store',
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
  })
  const body = await response.json().catch(() => ({}))
  const payload = unwrap(body)
  if (!response.ok || !payload.ok) {
    if (response.status === 401 && reauthorizeAfterUnauthorized()) {
      const error = new Error('reauthorizing')
      error.reauthorizing = true
      throw error
    }
    const error = new Error(String(payload.error || `HTTP ${response.status}`))
    error.status = response.status
    throw error
  }
  clearOidcReauthorization()
  return payload.data
}

function formatTime(value) {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleString('zh-CN', { hour12: false })
}

function normalizeEnvironment(value) {
  if (value === 'development') return 'dev'
  if (value === 'production') return 'prod'
  return value || 'shared'
}

function environmentLabel(value, tx) {
  const normalized = normalizeEnvironment(value)
  if (normalized === 'dev') return tx({ zh: '开发', en: 'Dev' })
  if (normalized === 'prod') return tx({ zh: '生产', en: 'Prod' })
  return tx({ zh: '共享', en: 'Shared' })
}

function healthView(health, tx) {
  if (health?.healthy === true) {
    return {
      label: tx({ zh: 'Healthy', en: 'Healthy' }),
      dot: 'bg-emerald-500',
      badge: 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-900 dark:bg-emerald-950/50 dark:text-emerald-300',
    }
  }
  if (health?.healthy === false) {
    return {
      label: tx({ zh: '异常', en: 'Unhealthy' }),
      dot: 'bg-rose-500',
      badge: 'border-rose-200 bg-rose-50 text-rose-800 dark:border-rose-900 dark:bg-rose-950/50 dark:text-rose-300',
    }
  }
  return {
    label: tx({ zh: '未知', en: 'Unknown' }),
    dot: 'bg-slate-400',
    badge: 'border-slate-200 bg-slate-50 text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300',
  }
}

function Metric({ label, value, detail, icon: Icon, tone }) {
  const tones = {
    cyan: 'bg-cyan-50 text-cyan-700 dark:bg-cyan-950/40 dark:text-cyan-300',
    green: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300',
    rose: 'bg-rose-50 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300',
    amber: 'bg-amber-50 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300',
  }
  return (
    <Card className="p-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.14em] text-brand-text dark:text-slate-400">{label}</p>
          <p className="mt-3 text-3xl font-black tabular-nums text-brand-textDark dark:text-white">{value}</p>
          <p className="mt-1 text-xs font-bold text-brand-text dark:text-slate-400">{detail}</p>
        </div>
        <div className={`rounded-2xl p-3 ${tones[tone] || tones.cyan}`}><Icon size={21} /></div>
      </div>
    </Card>
  )
}

function formatUsers(service) {
  return service.userCountSource === 'iam-app-bindings' && Number.isFinite(Number(service.userCount))
    ? Number(service.userCount).toLocaleString('zh-CN')
    : '—'
}

function formatLatency(health) {
  return Number.isFinite(Number(health?.latencyMs)) ? `${health.latencyMs} ms` : '—'
}

function backupView(dataProtection, tx) {
  const readiness = dataProtection?.readiness || 'not-applicable'
  if (readiness === 'verified') return { label: tx({ zh: '已验证', en: 'Verified' }), badge: 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-900 dark:bg-emerald-950/50 dark:text-emerald-300' }
  if (readiness === 'blocked') return { label: tx({ zh: '阻塞', en: 'Blocked' }), badge: 'border-rose-200 bg-rose-50 text-rose-800 dark:border-rose-900 dark:bg-rose-950/50 dark:text-rose-300' }
  if (readiness === 'pending') return { label: tx({ zh: '待验证', en: 'Pending' }), badge: 'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-900 dark:bg-amber-950/50 dark:text-amber-300' }
  return { label: tx({ zh: '不适用', en: 'N/A' }), badge: 'border-slate-200 bg-slate-50 text-slate-600 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-400' }
}

function backupDetail(dataProtection, tx) {
  const datastores = dataProtection?.datastores || []
  if (!datastores.length) return tx({ zh: '无声明的数据存储', en: 'No declared datastore' })
  const provider = datastores[0]?.destination?.provider === 'backblaze-b2' ? 'Backblaze B2' : datastores[0]?.destination?.provider || '—'
  return `${provider} · ${datastores.map((item) => item.id).join(', ')}`
}

function directoryDetail(environment, tx) {
  if (environment === 'production') return tx({ zh: '生产 IAM directory 总数', en: 'Production IAM directory total' })
  if (environment === 'development') return tx({ zh: '开发 IAM directory 总数', en: 'Development IAM directory total' })
  return tx({ zh: 'IAM directory 总数', en: 'IAM directory total' })
}

function backupSummary(summary, hasData, tx) {
  const destination = hasData && Array.isArray(summary.backupDestinations) ? summary.backupDestinations[0] : null
  if (!destination) {
    return {
      title: tx({ zh: '备份目标：—', en: 'Backup target: —' }),
      detail: tx({ zh: '尚未从后端取得备份治理状态。', en: 'Backup governance status has not been received from the backend.' }),
    }
  }
  const provider = destination.provider === 'backblaze-b2' ? 'Backblaze B2' : destination.provider || '—'
  const scope = [destination.dedicated ? tx({ zh: '独立', en: 'dedicated' }) : '', destination.private ? tx({ zh: '私有', en: 'private' }) : ''].filter(Boolean).join(' ')
  const controls = [
    destination.clientSideEncryption ? tx({ zh: '客户端加密', en: 'client-side encryption' }) : '',
    destination.serverSideEncryption || '',
    destination.objectLockRequired ? 'Object Lock' : '',
  ].filter(Boolean).join(' + ')
  return {
    title: tx({ zh: `备份目标：${scope ? `${scope} ` : ''}${provider} Bucket`, en: `Backup target: ${scope ? `${scope} ` : ''}${provider} bucket` }),
    detail: tx({
      zh: `治理清单状态：${destination.status || 'unknown'}${controls ? ` · ${controls}` : ''}；这不是备份对象或恢复演练的实时证明。`,
      en: `Governance status: ${destination.status || 'unknown'}${controls ? ` · ${controls}` : ''}; this is not live proof of backup objects or restore drills.`,
    }),
  }
}

function ServiceCard({ service, tx }) {
  const health = healthView(service.health, tx)
  const users = formatUsers(service)
  const latency = formatLatency(service.health)
  const backup = backupView(service.dataProtection, tx)
  return (
    <Card className="overflow-hidden">
      <div className="p-5 sm:p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className={`h-2.5 w-2.5 rounded-full ${health.dot}`} aria-hidden="true" />
              <h2 className="truncate text-lg font-black text-brand-textDark dark:text-white">{service.id}</h2>
              <span className={`rounded-full border px-2.5 py-1 text-[11px] font-black ${health.badge}`}>{health.label}</span>
            </div>
            <div className="mt-2 flex flex-wrap gap-2 text-xs font-extrabold text-brand-text dark:text-slate-400">
              <span>{service.owner}</span><span>·</span><span>{service.kind}</span><span>·</span>
              <span>{environmentLabel(service.environment, tx)}</span>
            </div>
          </div>
          {service.primaryUrl ? (
            <a href={service.primaryUrl} target="_blank" rel="noreferrer" className="inline-flex shrink-0 items-center gap-2 text-sm font-black text-cyan-700 hover:text-cyan-900 dark:text-cyan-300 dark:hover:text-cyan-200">
              {tx({ zh: '打开平台', en: 'Open' })}<ExternalLink size={15} />
            </a>
          ) : null}
        </div>

        <div className="mt-5 grid grid-cols-2 gap-3 rounded-2xl border-2 border-brand-textDark/5 bg-brand-bg/60 p-4 dark:border-slate-800 dark:bg-slate-900/60 sm:grid-cols-4">
          <div><p className="text-[11px] font-black uppercase tracking-wider text-brand-text">HTTP</p><p className="mt-1 font-mono text-sm font-black text-brand-textDark dark:text-white">{service.health?.status ?? '—'}</p></div>
          <div><p className="text-[11px] font-black uppercase tracking-wider text-brand-text">Latency</p><p className="mt-1 font-mono text-sm font-black text-brand-textDark dark:text-white">{latency}</p></div>
          <div><p className="text-[11px] font-black uppercase tracking-wider text-brand-text">{tx({ zh: '绑定用户', en: 'Bound users' })}</p><p className="mt-1 font-mono text-sm font-black text-brand-textDark dark:text-white">{users}</p></div>
          <div><p className="text-[11px] font-black uppercase tracking-wider text-brand-text">{tx({ zh: '检查时间', en: 'Checked' })}</p><p className="mt-1 text-xs font-black text-brand-textDark dark:text-white">{formatTime(service.health?.checkedAt)}</p></div>
        </div>

        <dl className="mt-5 space-y-3 text-sm">
          <div className="grid gap-1 sm:grid-cols-[110px_1fr]"><dt className="font-black text-brand-text">URL</dt><dd className="break-all font-mono text-xs font-bold text-brand-textDark dark:text-slate-200">{service.primaryUrl || '—'}</dd></div>
          <div className="grid gap-1 sm:grid-cols-[110px_1fr]"><dt className="font-black text-brand-text">IAM</dt><dd className="break-words text-xs font-bold text-brand-textDark dark:text-slate-200">{service.iamAudiences?.join(', ') || tx({ zh: '无 IAM audience', en: 'No IAM audience' })}</dd></div>
          <div className="grid gap-1 sm:grid-cols-[110px_1fr]"><dt className="font-black text-brand-text">Backup</dt><dd className="flex flex-wrap items-center gap-2"><span className={`rounded-full border px-2.5 py-1 text-[11px] font-black ${backup.badge}`}>{backup.label}</span><span className="break-words text-xs font-bold text-brand-textDark dark:text-slate-200">{backupDetail(service.dataProtection, tx)}</span></dd></div>
          {service.health?.error ? <div className="grid gap-1 sm:grid-cols-[110px_1fr]"><dt className="font-black text-rose-600">Error</dt><dd className="break-words font-mono text-xs font-bold text-rose-700 dark:text-rose-300">{service.health.error}</dd></div> : null}
        </dl>
      </div>
    </Card>
  )
}


function ServiceTable({ services, tx }) {
  return (
    <section className="overflow-hidden rounded-3xl border-2 border-brand-textDark/10 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-950">
      <div className="overflow-x-auto" tabIndex={0} role="region" aria-label={tx({ zh: '平台服务表格，可横向滚动', en: 'Platform service table; horizontally scrollable' })}>
        <table className="min-w-[1660px] w-full border-collapse text-left text-sm">
          <caption className="sr-only">{tx({ zh: '平台注册服务、用户、备份与实时健康状态', en: 'Registered platform services, users, backup, and live health status' })}</caption>
          <thead className="border-b-2 border-brand-textDark/10 bg-brand-bg/80 dark:border-slate-800 dark:bg-slate-900/80">
            <tr className="text-[11px] font-black uppercase tracking-[0.08em] text-brand-text dark:text-slate-400">
              <th className="sticky left-0 z-10 min-w-[220px] bg-brand-bg px-5 py-4 dark:bg-slate-900">{tx({ zh: '服务', en: 'Service' })}</th>
              <th className="min-w-[90px] px-4 py-4">{tx({ zh: '环境', en: 'Environment' })}</th>
              <th className="min-w-[170px] px-4 py-4">{tx({ zh: 'Owner / 类型', en: 'Owner / type' })}</th>
              <th className="min-w-[260px] px-4 py-4">{tx({ zh: '主链接', en: 'Primary link' })}</th>
              <th className="min-w-[90px] px-4 py-4 text-right">{tx({ zh: '用户数', en: 'Users' })}</th>
              <th className="min-w-[190px] px-4 py-4">Backup</th>
              <th className="min-w-[120px] px-4 py-4">Healthy</th>
              <th className="min-w-[90px] px-4 py-4">HTTP</th>
              <th className="min-w-[100px] px-4 py-4">{tx({ zh: '延迟', en: 'Latency' })}</th>
              <th className="min-w-[220px] px-4 py-4">{tx({ zh: '错误', en: 'Error' })}</th>
              <th className="min-w-[180px] px-4 py-4">{tx({ zh: '最近检查', en: 'Last checked' })}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-brand-textDark/10 dark:divide-slate-800">
            {services.map((service) => {
              const health = healthView(service.health, tx)
              const backup = backupView(service.dataProtection, tx)
              return (
                <tr key={service.id} className="align-top transition-colors hover:bg-cyan-50/40 dark:hover:bg-cyan-950/10">
                  <td className="sticky left-0 z-[1] bg-white px-5 py-4 shadow-[1px_0_0_rgba(15,23,42,0.08)] dark:bg-slate-950 dark:shadow-[1px_0_0_rgb(30,41,59)]">
                    <p className="font-black text-brand-textDark dark:text-white">{service.id}</p>
                    <p className="mt-1 max-w-[230px] truncate text-[11px] font-bold text-brand-text dark:text-slate-400" title={service.iamAudiences?.join(', ') || ''}>
                      {service.iamAudiences?.join(', ') || tx({ zh: '无 IAM audience', en: 'No IAM audience' })}
                    </p>
                  </td>
                  <td className="px-4 py-4">
                    <span className="inline-flex rounded-full border border-brand-textDark/10 bg-brand-bg px-2.5 py-1 text-xs font-black text-brand-textDark dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200">
                      {environmentLabel(service.environment, tx)}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <p className="font-black text-brand-textDark dark:text-slate-100">{service.owner || '—'}</p>
                    <p className="mt-1 text-xs font-bold text-brand-text dark:text-slate-400">{service.kind || '—'}</p>
                  </td>
                  <td className="px-4 py-4">
                    {service.primaryUrl ? (
                      <a href={service.primaryUrl} target="_blank" rel="noreferrer" className="inline-flex max-w-[280px] items-start gap-1.5 break-all font-mono text-xs font-bold text-cyan-700 hover:underline dark:text-cyan-300">
                        <span>{service.primaryUrl}</span><ExternalLink className="mt-0.5 shrink-0" size={13} />
                      </a>
                    ) : '—'}
                  </td>
                  <td className="px-4 py-4 text-right font-mono font-black tabular-nums text-brand-textDark dark:text-slate-100">{formatUsers(service)}</td>
                  <td className="px-4 py-4"><span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-black ${backup.badge}`}>{backup.label}</span><p className="mt-1 max-w-[210px] truncate text-[11px] font-bold text-brand-text dark:text-slate-400" title={backupDetail(service.dataProtection, tx)}>{backupDetail(service.dataProtection, tx)}</p></td>
                  <td className="px-4 py-4">
                    <span className={`inline-flex items-center gap-2 rounded-full border px-2.5 py-1 text-xs font-black ${health.badge}`}>
                      <span className={`h-2 w-2 rounded-full ${health.dot}`} aria-hidden="true" />{health.label}
                    </span>
                  </td>
                  <td className="px-4 py-4 font-mono font-black tabular-nums text-brand-textDark dark:text-slate-100">{service.health?.status ?? '—'}</td>
                  <td className="px-4 py-4 font-mono font-black tabular-nums text-brand-textDark dark:text-slate-100">{formatLatency(service.health)}</td>
                  <td className="px-4 py-4">
                    <p className={`max-w-[280px] break-words font-mono text-xs font-bold ${service.health?.error ? 'text-rose-700 dark:text-rose-300' : 'text-brand-text dark:text-slate-500'}`}>
                      {service.health?.error || '—'}
                    </p>
                  </td>
                  <td className="px-4 py-4 text-xs font-bold text-brand-textDark dark:text-slate-300">{formatTime(service.health?.checkedAt)}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </section>
  )
}

export default function PlatformRegistry() {
  const { tx } = useI18n()
  const session = useMemo(() => loadSession(), [])
  const token = String(session?.tokens?.accessToken || session?.tokens?.idToken || '')
  const [state, setState] = useState({ loading: true, refreshing: false, error: '', status: 0, data: null })
  const [filters, setFilters] = useState({ query: '', owner: 'all', environment: 'all', health: 'all' })
  const [viewMode, setViewMode] = useState('table')

  const refresh = useCallback(async (quiet = false) => {
    if (!token) return setState({ loading: false, refreshing: false, error: 'missing_token', status: 401, data: null })
    setState((current) => ({ ...current, loading: quiet ? current.loading : !current.data, refreshing: true, error: '' }))
    try {
      const data = await loadRegistry(token)
      setState({ loading: false, refreshing: false, error: '', status: 200, data })
    } catch (error) {
      if (error?.reauthorizing) return
      setState((current) => ({
        ...current,
        loading: false,
        refreshing: false,
        error: String(error?.message || error),
        status: error?.status || 0,
        data: error?.status === 403 ? null : current.data,
      }))
    }
  }, [token])

  useEffect(() => {
    refresh()
    const timer = setInterval(() => refresh(true), AUTO_REFRESH_MS)
    return () => clearInterval(timer)
  }, [refresh])

  const services = Array.isArray(state.data?.services) ? state.data.services : []
  const owners = useMemo(() => Array.from(new Set(services.map((item) => item.owner).filter(Boolean))).sort(), [services])
  const filtered = useMemo(() => {
    const query = filters.query.trim().toLowerCase()
    return services.filter((service) => {
      const searchable = [service.id, service.owner, service.kind, service.primaryUrl, ...(service.iamAudiences || [])].join(' ').toLowerCase()
      if (query && !searchable.includes(query)) return false
      if (filters.owner !== 'all' && service.owner !== filters.owner) return false
      if (filters.environment !== 'all' && normalizeEnvironment(service.environment) !== filters.environment) return false
      if (filters.health === 'healthy' && service.health?.healthy !== true) return false
      if (filters.health === 'unhealthy' && service.health?.healthy !== false) return false
      if (filters.health === 'unknown' && service.health?.healthy != null) return false
      return true
    })
  }, [services, filters])

  const summary = state.data?.summary || {}
  const permissionDenied = state.status === 403
  const hasData = Boolean(state.data)
  const stale = hasData && Boolean(state.error)
  const issueCount = hasData ? Number(summary.unhealthyServices || 0) + Number(summary.unknownServices || 0) : '—'
  const backup = backupSummary(summary, hasData, tx)

  return (
    <div className="space-y-6">
      <header className="flex flex-col gap-5 xl:flex-row xl:items-end xl:justify-between">
        <div>
          <div className="flex items-center gap-3 text-xs font-black uppercase tracking-[0.18em] text-cyan-700 dark:text-cyan-300"><Boxes size={17} />Platform Registry</div>
          <h1 className="mt-3 text-3xl font-black tracking-tight text-brand-textDark dark:text-white sm:text-4xl">{tx({ zh: '平台注册服务总览', en: 'Platform service registry' })}</h1>
          <p className="mt-2 max-w-3xl text-sm font-bold leading-6 text-brand-text dark:text-slate-400">{tx({ zh: '来自平台清单、IAM 与实时健康探针。状态按清单中的预期 HTTP 响应判断，每 30 秒自动刷新。', en: 'Manifest registry, IAM counts, and live health probes. Expected HTTP statuses define health; refreshes every 30 seconds.' })}</p>
        </div>
        <div className="flex items-center gap-3"><span className="text-xs font-bold text-brand-text">{tx({ zh: '最近更新', en: 'Updated' })} {formatTime(state.data?.checkedAt)}</span><Button variant="neutral" size="sm" disabled={state.refreshing} onClick={() => refresh(true)}><RefreshCw size={16} className={state.refreshing ? 'animate-spin' : ''} />{tx({ zh: '刷新', en: 'Refresh' })}</Button></div>
      </header>

      {state.error ? <div className="flex items-start gap-3 rounded-2xl border-2 border-rose-200 bg-rose-50 p-4 text-rose-900 dark:border-rose-900 dark:bg-rose-950/40 dark:text-rose-200"><AlertTriangle className="mt-0.5 shrink-0" size={18} /><div><p className="font-black">{permissionDenied ? tx({ zh: '需要 ADMIN 权限', en: 'ADMIN permission required' }) : stale ? tx({ zh: '刷新失败，正在显示上次成功数据', en: 'Refresh failed; showing the last successful data' }) : tx({ zh: '无法读取平台状态', en: 'Could not load platform status' })}</p>{stale ? <p className="mt-1 text-xs font-bold">{tx({ zh: '当前表格数据可能已经过期，请检查后端后重试。', en: 'The displayed table may be stale; check the backend and retry.' })}</p> : null}<p className="mt-1 break-words font-mono text-xs">{state.error}</p></div></div> : null}

      <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Metric label={tx({ zh: '注册服务', en: 'Registered' })} value={hasData ? summary.totalServices ?? services.length : '—'} detail={tx({ zh: '平台清单中的活跃服务', en: 'Active manifest services' })} icon={Boxes} tone="cyan" />
        <Metric label="Healthy" value={hasData ? summary.healthyServices ?? '—' : '—'} detail={tx({ zh: '符合预期探针响应', en: 'Expected probe response' })} icon={Activity} tone="green" />
        <Metric label={tx({ zh: '异常 / 未知', en: 'Issues / unknown' })} value={issueCount} detail={tx({ zh: '需要进一步检查', en: 'Needs investigation' })} icon={AlertTriangle} tone="rose" />
        <Metric label={tx({ zh: 'IAM 用户', en: 'IAM users' })} value={hasData ? summary.totalUsers ?? '—' : '—'} detail={directoryDetail(summary.userDirectoryEnvironment, tx)} icon={Users} tone="amber" />
      </section>

      <section className="flex flex-col gap-3 rounded-3xl border-2 border-amber-200 bg-amber-50/70 p-4 text-sm dark:border-amber-900 dark:bg-amber-950/20 sm:flex-row sm:items-center sm:justify-between">
        <div><p className="font-black text-amber-950 dark:text-amber-200">{backup.title}</p><p className="mt-1 text-xs font-bold text-amber-800 dark:text-amber-300">{backup.detail}</p></div>
        <div className="flex flex-wrap gap-2 text-xs font-black"><span className="rounded-full bg-white px-3 py-1.5 text-amber-900 dark:bg-slate-900 dark:text-amber-200">{tx({ zh: '待验证', en: 'Pending' })} {hasData ? summary.backupPendingServices ?? '—' : '—'}</span><span className="rounded-full bg-white px-3 py-1.5 text-rose-700 dark:bg-slate-900 dark:text-rose-300">{tx({ zh: '阻塞', en: 'Blocked' })} {hasData ? summary.backupBlockedServices ?? '—' : '—'}</span></div>
      </section>

      {hasData ? <section className="rounded-3xl border-2 border-brand-textDark/10 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-950">
        <div className="grid gap-3 lg:grid-cols-[1fr_repeat(3,minmax(130px,auto))]">
          <label className="relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-text" size={17} /><input value={filters.query} onChange={(event) => setFilters((current) => ({ ...current, query: event.target.value }))} placeholder={tx({ zh: '搜索服务、Owner、URL 或 audience…', en: 'Search service, owner, URL, or audience…' })} className="w-full rounded-2xl border-2 border-brand-textDark/10 bg-brand-bg py-2.5 pl-10 pr-3 text-sm font-bold outline-none focus:border-cyan-500 dark:border-slate-800 dark:bg-slate-900" /></label>
          <select value={filters.owner} onChange={(event) => setFilters((current) => ({ ...current, owner: event.target.value }))} className="rounded-2xl border-2 border-brand-textDark/10 bg-white px-3 py-2.5 text-sm font-bold dark:border-slate-800 dark:bg-slate-900"><option value="all">{tx({ zh: '全部 Owner', en: 'All owners' })}</option>{owners.map((owner) => <option key={owner} value={owner}>{owner}</option>)}</select>
          <select value={filters.environment} onChange={(event) => setFilters((current) => ({ ...current, environment: event.target.value }))} className="rounded-2xl border-2 border-brand-textDark/10 bg-white px-3 py-2.5 text-sm font-bold dark:border-slate-800 dark:bg-slate-900"><option value="all">{tx({ zh: '全部环境', en: 'All environments' })}</option><option value="prod">Prod</option><option value="dev">Dev</option><option value="shared">Shared</option></select>
          <select value={filters.health} onChange={(event) => setFilters((current) => ({ ...current, health: event.target.value }))} className="rounded-2xl border-2 border-brand-textDark/10 bg-white px-3 py-2.5 text-sm font-bold dark:border-slate-800 dark:bg-slate-900"><option value="all">{tx({ zh: '全部状态', en: 'All health' })}</option><option value="healthy">Healthy</option><option value="unhealthy">Unhealthy</option><option value="unknown">Unknown</option></select>
        </div>
        <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-xs font-bold text-brand-text">{filtered.length} / {services.length} {tx({ zh: '个服务', en: 'services' })}</div>
          <div className="inline-flex w-fit rounded-xl border-2 border-brand-textDark/10 bg-brand-bg p-1 dark:border-slate-800 dark:bg-slate-900" role="group" aria-label={tx({ zh: '视图切换', en: 'View switcher' })}>
            <button type="button" aria-pressed={viewMode === 'card'} onClick={() => setViewMode('card')} className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-black transition-colors ${viewMode === 'card' ? 'bg-white text-brand-textDark shadow-sm dark:bg-slate-800 dark:text-white' : 'text-brand-text hover:text-brand-textDark dark:hover:text-white'}`}><LayoutGrid size={15} />{tx({ zh: '卡片', en: 'Card' })}</button>
            <button type="button" aria-pressed={viewMode === 'table'} onClick={() => setViewMode('table')} className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-black transition-colors ${viewMode === 'table' ? 'bg-white text-brand-textDark shadow-sm dark:bg-slate-800 dark:text-white' : 'text-brand-text hover:text-brand-textDark dark:hover:text-white'}`}><Table2 size={15} />{tx({ zh: '表格', en: 'Table' })}</button>
          </div>
        </div>
      </section> : null}

      {state.loading && !hasData ? <div className="py-16 text-center text-sm font-black text-brand-text">{tx({ zh: '正在查询平台注册信息和实时健康状态…', en: 'Loading registry and live health…' })}</div> : null}
      {hasData && filtered.length > 0 && viewMode === 'card' ? <section className="grid gap-4 xl:grid-cols-2">{filtered.map((service) => <ServiceCard key={service.id} service={service} tx={tx} />)}</section> : null}
      {hasData && filtered.length > 0 && viewMode === 'table' ? <ServiceTable services={filtered} tx={tx} /> : null}
      {hasData && filtered.length === 0 ? <div className="rounded-3xl border-2 border-dashed border-brand-textDark/10 py-14 text-center text-sm font-black text-brand-text dark:border-slate-800">{tx({ zh: '没有符合筛选条件的服务。', en: 'No services match the filters.' })}</div> : null}
    </div>
  )
}
